import React, { useState, useRef, useEffect } from 'react';
import { Window } from '@gfazioli/mantine-window';
import {
    ActionIcon,
    Card,
    Flex,
    Group,
    Portal,
    Stack,
    Title,
    Text,
    Loader,
} from '@mantine/core';
import {
    IconZoomIn,
    IconZoomOut,
    IconRotate2,
    IconRotateClockwise2,
    IconChevronLeft,
    IconChevronRight,
    IconDownload,
    IconX,
} from '@tabler/icons-react';
import SuperAdminS3Path from '../../common/SuperAdminS3Path';

export type ProofDocumentForModal = {
    details?: {
        fileName?: string | null;
        fileType?: string | null;
    } | null;
    downloadUrl: string | null;
    documentS3Path?: string | null;
};

type Props = {
    open: boolean;
    documents: ProofDocumentForModal[];
    initialIndex: number;
    onClose: () => void;
    layout?: 'fixed' | 'floating';
};

const ZOOM_SCALE = 2;

const FLOATING_CARD_WIDTH = 560;
const VIEWPORT_MARGIN = 16;

const PROOF_WINDOW_ROOT_CLASS =
    '!z-[1100] overflow-hidden rounded-lg border-2 border-solid border-[#00539F] bg-white p-0 shadow-[0_16px_48px_-12px_rgba(0,83,159,0.28),0_8px_24px_-8px_rgba(0,0,0,0.12)]';

const PROOF_WINDOW_CONTENT_CLASS =
    'flex min-h-0 flex-1 flex-col overflow-hidden border-0 p-0';

function ProofOfPricingModal({
    open,
    documents,
    initialIndex,
    onClose,
    layout = 'fixed',
}: Props) {
    const [isZoomed, setIsZoomed] = useState(false);
    const [isPdfLoading, setIsPdfLoading] = useState(true);
    const [pdfLoadError, setPdfLoadError] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
        null,
    );
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const currentDocument = documents[currentIndex];
    const documentType =
        currentDocument?.details?.fileType?.toLowerCase() || '';
    const isImage = documentType.includes('image');
    const isPdf = documentType.includes('pdf');
    const hasDownloadUrl = Boolean(currentDocument?.downloadUrl);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    useEffect(() => {
        setIsPdfLoading(true);
        setPdfLoadError(false);
    }, [currentIndex]);

    const resetImageState = () => {
        setIsZoomed(false);
        setRotation(0);
        setPosition({ x: 0, y: 0 });
    };

    const handlePrevious = () => {
        const newIndex = currentIndex - 1;
        const updatedIndex = newIndex < 0 ? documents.length - 1 : newIndex;
        setCurrentIndex(updatedIndex);
        resetImageState();
    };

    const handleNext = () => {
        setCurrentIndex((currentIndex + 1) % documents.length);
        resetImageState();
    };

    const toggleZoom = () => {
        if (isZoomed) {
            setPosition({ x: 0, y: 0 });
        }
        setIsZoomed(!isZoomed);
    };

    const handleRotateClockwise = () => {
        setRotation(prevRotation => prevRotation + 90);
        setPosition({ x: 0, y: 0 });
    };

    const handleRotateCounterclockwise = () => {
        setRotation(prevRotation => prevRotation - 90);
        setPosition({ x: 0, y: 0 });
    };

    const handleDownload = () => {
        if (currentDocument?.downloadUrl) {
            window.open(currentDocument.downloadUrl, '_blank');
        }
    };

    const calculateBoundaries = (newX: number, newY: number) => {
        if (!containerRef.current || !imageRef.current) {
            return { x: 0, y: 0 };
        }

        const container = containerRef.current.getBoundingClientRect();
        const image = imageRef.current.getBoundingClientRect();

        const maxX = (image.width * ZOOM_SCALE - container.width) / 2;
        const maxY = (image.height * ZOOM_SCALE - container.height) / 2;

        const isVertical = rotation % 180 !== 0;
        const actualMaxX = isVertical ? maxY : maxX;
        const actualMaxY = isVertical ? maxX : maxY;

        return {
            x: Math.max(Math.min(newX, actualMaxX), -actualMaxX),
            y: Math.max(Math.min(newY, actualMaxY), -actualMaxY),
        };
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (isZoomed) {
            e.stopPropagation();
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y,
            });
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!dragStart || !isZoomed) return;

        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        const boundedPosition = calculateBoundaries(newX, newY);
        setPosition(boundedPosition);
    };

    const handleMouseUp = () => {
        setDragStart(null);
    };

    const renderNavigationButtons = () => {
        if (documents.length <= 1) return null;

        return (
            <>
                <ActionIcon
                    variant="light"
                    style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                    }}
                    onClick={handlePrevious}
                    aria-label="Previous proof of pricing"
                >
                    <IconChevronLeft size={24} />
                </ActionIcon>
                <ActionIcon
                    variant="light"
                    style={{
                        position: 'absolute',
                        right: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                    }}
                    onClick={handleNext}
                    aria-label="Next proof of pricing"
                >
                    <IconChevronRight size={24} />
                </ActionIcon>
            </>
        );
    };

    const renderImageContent = () => {
        if (!currentDocument?.downloadUrl) {
            return (
                <Flex align="center" justify="center" className="h-full">
                    <Text size="lg" c="dimmed">
                        Image not available
                    </Text>
                </Flex>
            );
        }

        const transformStyle = {
            transform: `translate(${position.x}px, ${position.y}px) scale(${
                isZoomed ? ZOOM_SCALE : 1
            }) rotate(${rotation}deg)`,
            transition: dragStart ? 'none' : 'transform 0.2s ease-out',
            transformOrigin: 'center center',
            willChange: 'transform',
        };

        return (
            <div
                className="absolute top-1/2 left-1/2 h-full flex items-center justify-center"
                style={{ transform: 'translate(-50%, -50%)' }}
            >
                <figure className="m-0 h-full flex items-center justify-center">
                    <div className="h-full flex items-center justify-center">
                        <img
                            ref={imageRef}
                            src={currentDocument.downloadUrl}
                            style={{
                                ...transformStyle,
                                maxHeight: '100%',
                                width: 'auto',
                                objectFit: 'contain',
                            }}
                            draggable={false}
                            alt="Proof of pricing"
                        />
                    </div>
                </figure>
            </div>
        );
    };

    const renderPdfContent = () => {
        if (!currentDocument?.downloadUrl) {
            return (
                <Flex align="center" justify="center" className="h-full">
                    <Text size="lg" c="dimmed">
                        File not available
                    </Text>
                </Flex>
            );
        }

        return (
            <Flex
                style={{ position: 'relative', width: '100%', height: '100%' }}
            >
                {isPdfLoading && (
                    <Flex
                        align="center"
                        justify="center"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: '#f1f3f5',
                            zIndex: 1,
                        }}
                    >
                        <Loader size="lg" />
                    </Flex>
                )}
                {pdfLoadError && (
                    <Flex
                        align="center"
                        justify="center"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: '#f1f3f5',
                            zIndex: 1,
                        }}
                    >
                        <Text size="lg" color="dimmed">
                            Failed to load PDF
                        </Text>
                    </Flex>
                )}
                <iframe
                    title="Proof of pricing"
                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                        currentDocument.downloadUrl,
                    )}&embedded=true`}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                    }}
                    onLoad={() => setIsPdfLoading(false)}
                    onError={() => {
                        setIsPdfLoading(false);
                        setPdfLoadError(true);
                    }}
                />
            </Flex>
        );
    };

    const renderUnsupportedContent = () => (
        <Flex align="center" justify="center" style={{ height: '100%' }}>
            <Text size="lg" color="dimmed">
                Unsupported file type: {documentType}
            </Text>
        </Flex>
    );

    const renderContent = () => {
        if (isImage) return renderImageContent();
        if (isPdf) return renderPdfContent();
        return renderUnsupportedContent();
    };

    const renderImageControls = () => {
        if (!isImage) return null;

        return (
            <>
                <ActionIcon
                    variant="subtle"
                    color="primary"
                    onClick={toggleZoom}
                    aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
                >
                    {isZoomed ? (
                        <IconZoomOut size={18} />
                    ) : (
                        <IconZoomIn size={18} />
                    )}
                </ActionIcon>
                <ActionIcon
                    variant="subtle"
                    color="primary"
                    onClick={handleRotateCounterclockwise}
                    aria-label="Rotate counterclockwise"
                >
                    <IconRotate2 size={18} />
                </ActionIcon>
                <ActionIcon
                    variant="subtle"
                    color="primary"
                    onClick={handleRotateClockwise}
                    aria-label="Rotate clockwise"
                >
                    <IconRotateClockwise2 size={18} />
                </ActionIcon>
            </>
        );
    };

    const headerTitleRow = (
        <Group justify="space-between" wrap="nowrap" align="center">
            <Group gap="xs" wrap="nowrap" className="min-w-0 flex-1">
                <Title order={4} className="truncate text-gray-900">
                    Proof Of Pricing
                </Title>
                <Text size="sm" className="shrink-0 text-gray-400">
                    {currentIndex + 1} / {documents.length}
                </Text>
            </Group>
            <Group gap="xs" wrap="nowrap" className="shrink-0">
                {renderImageControls()}
                <SuperAdminS3Path
                    path={currentDocument?.documentS3Path}
                    bucket="documents"
                />
                <ActionIcon
                    variant="subtle"
                    color="primary"
                    onClick={handleDownload}
                    disabled={!hasDownloadUrl}
                    aria-label={
                        hasDownloadUrl
                            ? 'Download proof of pricing'
                            : 'Proof file unavailable'
                    }
                    title={hasDownloadUrl ? 'Download' : 'File not available'}
                >
                    <IconDownload size={18} />
                </ActionIcon>
                <ActionIcon
                    variant="subtle"
                    color="primary"
                    onClick={onClose}
                    title="Close"
                >
                    <IconX size={18} />
                </ActionIcon>
            </Group>
        </Group>
    );

    const fileNameRow = (
        <Text className="font-bold text-gray-500" size="sm">
            {currentDocument?.details?.fileName ?? 'File'}
        </Text>
    );

    const proofWindowHeader = (
        <Stack
            gap={2}
            className="shrink-0 cursor-grab bg-white px-4 pb-2 pt-3 active:cursor-grabbing"
        >
            {headerTitleRow}
            {fileNameRow}
        </Stack>
    );

    const viewerBody = (
        <Flex
            ref={containerRef}
            className={`relative min-h-0 flex-1 overflow-hidden rounded-b-md bg-gray-50 ${
                isZoomed
                    ? 'cursor-grab active:cursor-grabbing'
                    : 'cursor-default'
            }`}
            style={{ flex: 1 }}
            onMouseDown={isImage ? handleMouseDown : undefined}
            onMouseMove={isImage ? handleMouseMove : undefined}
            onMouseUp={isImage ? handleMouseUp : undefined}
            onMouseLeave={isImage ? handleMouseUp : undefined}
        >
            {renderNavigationButtons()}
            {renderContent()}
        </Flex>
    );

    if (!open || !currentDocument) return null;

    if (layout === 'floating') {
        const floatingX = Math.max(
            VIEWPORT_MARGIN,
            Math.round(
                ((typeof window !== 'undefined' ? window.innerWidth : 800) -
                    FLOATING_CARD_WIDTH) *
                    0.35,
            ),
        );
        const floatingHeight =
            typeof window !== 'undefined'
                ? Math.round(window.innerHeight * 0.85)
                : 700;
        const floatingY = Math.max(
            VIEWPORT_MARGIN,
            Math.round(
                ((typeof window !== 'undefined' ? window.innerHeight : 800) -
                    floatingHeight) /
                    2,
            ),
        );

        return (
            <Window
                opened
                resizable="both"
                draggable="both"
                onClose={onClose}
                withinPortal
                withBorder={false}
                withCloseButton={false}
                withCollapseButton={false}
                withToolsButton={false}
                withScrollArea={false}
                defaultX={floatingX}
                defaultY={floatingY}
                defaultWidth={FLOATING_CARD_WIDTH}
                defaultHeight={floatingHeight}
                minWidth={280}
                minHeight={200}
                persistState={false}
                shadow="none"
                radius="lg"
                title="Proof Of Pricing"
                classNames={{
                    root: PROOF_WINDOW_ROOT_CLASS,
                    container:
                        'flex h-full flex-col overflow-hidden rounded-lg border-0 bg-white',
                    header: '!hidden !h-0 !min-h-0 !overflow-hidden !border-0 !p-0',
                    content: `${PROOF_WINDOW_CONTENT_CLASS} !h-auto !flex-1 !min-h-0`,
                }}
                styles={{
                    container: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    },
                    content: {
                        flex: '1 1 0%',
                        minHeight: 0,
                        height: 'auto',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <Stack gap={0} className="flex min-h-0 flex-1 flex-col">
                    {proofWindowHeader}
                    {viewerBody}
                </Stack>
            </Window>
        );
    }

    const fixedHeaderTitleRow = headerTitleRow;

    return (
        <Portal>
            <Card
                shadow="none"
                style={{
                    left: `calc((100vw - 850px)/2)`,
                    width: 'clamp(0px, 583px, calc(100vw - 850px))',
                    position: 'fixed',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    maxWidth: '1200px',
                    height: '85vh',
                    zIndex: 1100,
                }}
                className={`${PROOF_WINDOW_ROOT_CLASS} flex flex-col overflow-hidden p-0`}
            >
                <Stack h="100%" gap={0} className="min-h-0">
                    <Stack gap={2} className="shrink-0 bg-white px-4 pb-2 pt-3">
                        {fixedHeaderTitleRow}
                        {fileNameRow}
                    </Stack>
                    {viewerBody}
                </Stack>
            </Card>
        </Portal>
    );
}

export default ProofOfPricingModal;
